import React, { useEffect } from "react";
import { Card, CardBody, Col, Container, Form, Row } from "reactstrap";
import { BaseButton, BaseInput } from "Components/ui";
import { useFormik } from "formik";
import { SupplierValidationSchema, getErrorMessage } from "utils";
import { useGetSupplierById, useUpdateSupplier } from "hooks/useSuppliers";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import { useNavigate, useParams } from "react-router-dom";
import { AppRoutes } from "../../constants";
import Spinners from "Components/Common/Spinner";

const EditSupplier = () => {
  document.title = "Papermap | Edit supplier info";
  const { mutate: updateSupplier, isPending } = useUpdateSupplier();
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: supplierData, isLoading } = useGetSupplierById(id || "");

  const initialValues = {
    name: "",
    phone: "",
    email: "",
    address: "",
    notes: "",
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: SupplierValidationSchema,
    onSubmit: (values) => {
      if (!id) return;
      const payload = {
        id: id,
        data: {
          name: values.name,
          phone: values.phone,
          email: values.email,
          address: values.address,
          notes: values.notes,
        },
      };

      updateSupplier(payload, {
        onSuccess: () => {
          navigate(AppRoutes.SUPPLIERS);
          formik.resetForm();
        },
        onError: (error) => {
          console.error("Error updating supplier:", error);
        },
      });
    },
  });

  useEffect(() => {
    if (supplierData) {
      formik.setValues({
        name: supplierData.name,
        phone: supplierData.phone,
        email: supplierData.email,
        address: supplierData.address,
        notes: supplierData.notes,
      });
    }
  }, [supplierData]);

  const handleCancel = () => {
    navigate(AppRoutes.SUPPLIERS);
  };

  if (isLoading) {
    return <Spinners />;
  }

  return (
    <React.Fragment>
      <div className={`page-content`}>
        <Container fluid>
          {/* Breadcrumb */}
          <Breadcrumbs title="Suppliers" breadcrumbItem="Edit Supplier" />

          <Form onSubmit={formik.handleSubmit} autoComplete="off">
            <Row>
              <Col xs={12}>
                {/* Basic Information */}

                <Card>
                  <CardBody>
                    <Form onSubmit={formik.handleSubmit} autoComplete="off">
                      <Row>
                        <Col sm="12">
                          <div className="mb-3">
                            <BaseInput
                              label="Name*"
                              name="name"
                              type="text"
                              value={formik.values.name}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              invalid={
                                formik.touched.name && !!formik.errors.name
                              }
                              errorMessage={getErrorMessage(formik.errors.name)}
                            />
                          </div>
                        </Col>
                        <Col sm="6">
                          <div className="mb-3">
                            <BaseInput
                              label="Phone"
                              name="phone"
                              type="text"
                              value={formik.values.phone}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              invalid={
                                formik.touched.phone && !!formik.errors.phone
                              }
                              errorMessage={getErrorMessage(
                                formik.errors.phone
                              )}
                            />
                          </div>
                        </Col>
                        <Col sm="6">
                          <div className="mb-3">
                            <BaseInput
                              label="Email"
                              name="email"
                              type="email"
                              value={formik.values.email}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              invalid={
                                formik.touched.email && !!formik.errors.email
                              }
                              errorMessage={getErrorMessage(
                                formik.errors.email
                              )}
                            />
                          </div>
                        </Col>
                        <Col sm="6">
                          <div className="mb-3">
                            <BaseInput
                              label="Address"
                              name="address"
                              type="textarea"
                              value={formik.values.address}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              invalid={
                                formik.touched.address &&
                                !!formik.errors.address
                              }
                              errorMessage={getErrorMessage(
                                formik.errors.address
                              )}
                            />
                          </div>
                        </Col>
                        <Col sm="6">
                          <div className="mb-3">
                            <BaseInput
                              label="Notes"
                              name="notes"
                              type="textarea"
                              value={formik.values.notes}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              invalid={
                                formik.touched.notes && !!formik.errors.notes
                              }
                              errorMessage={getErrorMessage(
                                formik.errors.notes
                              )}
                            />
                          </div>
                        </Col>
                      </Row>
                    </Form>
                  </CardBody>
                </Card>
              </Col>
            </Row>
            {/* Button Section */}
            <div className="d-flex justify-content-end mt-3">
              <BaseButton
                type="submit"
                color="primary"
                className="w-20 me-1"
                style={{ width: "20%" }}
                loading={isPending}
              >
                Submit
              </BaseButton>
              <BaseButton
                color="secondary"
                className="w-20 ms-1"
                style={{ width: "20%" }}
                onClick={handleCancel}
              >
                Cancel
              </BaseButton>
            </div>
          </Form>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default EditSupplier;
